import React, { useState } from 'react';
import {
    Grid,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Checkbox,
    FormControlLabel,
    Button,
    Box,
    Typography,
    InputAdornment,
    Modal,
    IconButton,
    Autocomplete
} from '@mui/material';

import config from '../../config';
import { useEffect } from 'react';
import { dateToLocalString, getChoices, notifyError, notifySuccess } from '../../util';
import { translate } from '../../i18n/customI18nProvider';
import { allConsumptions, deleteConsumption, saveConsumption } from '../../services/consumption.service';

import { useNotify } from 'react-admin';
import { addBarConsumption } from '../../services/bar.service';
import { Clear, Close, Delete, Save } from '@mui/icons-material';
import { useRef } from 'react';
import { ColorSelect } from '../../components/input/ColorSelect';

const consumption_types = [
    { id: 'quantity', name: translate('custom.unit_quantity') },
    { id: 'input', name: translate('custom.quantity_to_be_entered') }
]

const defaultValues = {    
    name: '',
    price: '',
    min_age: '0',
    color: '',
    type: 'quantity',
    round: '',
    deposit: '',
    hidden: false,
    can_buy_only_with_voucher: false,
    tag: '',
    start_date: '',
    end_date: ''
}

const ConsumptionForm = ({ max_consumption_price, currencyCode, show, consumption, deposits, onClose, barId, barConsumptions }) => {
    const notify = useNotify();
    const [formData, setFormData] = useState(consumption || defaultValues);
    const [errors, setErrors] = useState({});
    const [existingConsumptions, setExistingConsumptions] = useState([]);
    const [disableAutocomplete, setDisableAutocomplete] = useState(false);

    const autoCompleteRef = useRef(null);
    const nameInputRef = useRef(null);
    
    useEffect(() => {
        if (consumption) {            
            setFormData({ ...defaultValues, ...consumption, ...{
                start_date: dateToLocalString(consumption.start_date),
                end_date: dateToLocalString(consumption.end_date),
            } });
            setDisableAutocomplete(true);
        } else {
            setFormData(defaultValues);
            setDisableAutocomplete(false);
        }
        setErrors({});
        loadExistingConsumptions();
    }, [show]);

    useEffect(() => {
        if(disableAutocomplete) {
            nameInputRef.current?.focus();
        } else {
            autoCompleteRef.current?.focus();
        }
    }, [disableAutocomplete]);
    
    const handleChange = (field, value) => {
        if(value != undefined) {
            setFormData(prev => ({
                ...prev,
                [field]: value
            }));
        }
        if(field == "name") {
            const checkValue = value ?? formData.name;
            const searchableConsumptions = existingConsumptions.filter(c => c.name.includes(checkValue));
            setDisableAutocomplete(searchableConsumptions.length < 1);
        }
    };

    const loadExistingConsumptions = async () => {
        if (!consumption) {
            const response = await allConsumptions();
            if(!response.error) {
                setExistingConsumptions(response);
            } else {
                setDisableAutocomplete(true);
            }
        }
    }
    
    const confirmForm = () => {
        let hasError = false;
        let newErrors = {};
        
        if (!formData.name || formData.name.trim() === '') {
            newErrors.name = translate('custom.errors.required_field');
            hasError = true;
        } else {
            const duplicate = barConsumptions.find(c => c.name === formData.name && (!consumption || c._id !== consumption._id));
            if (duplicate) {
                newErrors.name = translate('custom.errors.consumption_name_must_be_unique');
                hasError = true;
            }
        }

        const price = Number(formData.price);
        if (!Number.isFinite(price) || price < 0) {
            newErrors.price = translate('custom.errors.required_field');
            hasError = true;
        } else {
            if(price > max_consumption_price) {
                newErrors.price = translate('custom.max_consumption_price') + ": " + max_consumption_price + " " + currencyCode;
                hasError = true;
            }
        }
        
        setErrors(newErrors);
        
        return !hasError
    }
    
    const handleSave = async (e) => {
        if (!confirmForm()) return;
        let response;
        if(consumption) {
            response = await saveConsumption(consumption._id, formData);
        } else {
            response = await addBarConsumption(barId, formData);
        }
        if (response.error) {
            notifyError(response.error.error);
        } else {
            notifySuccess('custom.saved_successfully');
        }
        onClose();
    };
    
    const handleDelete = async () => {
        const response = await deleteConsumption(consumption._id);
        if (response.error) {
            notifyError(response.error.message);
        } else {
            notifySuccess('custom.deleted_successfully');
        }
        onClose();
    }
    
    const handleCancel = () => {
        onClose();
    };

    const handleNameChange = (event, newValue) => {
        const consumption = existingConsumptions.find(c => c.name === newValue);
        if (consumption) {
            loadConsumption(consumption);
        } else {
            setFormData(prev => ({ ...prev, name: newValue }));
        }
    }

    const handleAutoCompleteKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            const consumption = existingConsumptions.find(c => c.name === formData.name);
            if (consumption) {
                loadConsumption(consumption);
            } else {
                setDisableAutocomplete(true);
            }
        }
    }

    const loadConsumption = (consumption) => {
        setFormData({
            ...formData,
            name: consumption.name,
            price: consumption.price,
            deposit: consumption.deposit ? consumption.deposit._id : '',
            min_age: consumption.min_age ? String(consumption.min_age) : '0',
            type: consumption.type || 'quantity',
            round: consumption.round ? String(consumption.round) : '',
            hidden: consumption.hidden,
            can_buy_only_with_voucher: consumption.can_buy_only_with_voucher,
        });
        setErrors({});
        setDisableAutocomplete(true);
    }
    
    const renderColorOption = (color) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ height: "15px", width: "15px", marginRight: "5px", background: color.colorCode, borderRadius: "15px" }}></span>
            <p style={{ fontSize: "14px", margin: "0px" }}>{color.name.toUpperCase()}</p>
        </div>
    );
    
    const modalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '80%',
        maxWidth: 800,
        maxHeight: '80vh',
        overflowY: 'auto',
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 4,
        borderRadius: 2
    };
    
    return (
        <Modal
            open={show}
            onClose={onClose}
            aria-labelledby="consumption-form-title"
            aria-describedby="consumption-form-description"
        >
            <Box sx={modalStyle}>
                <div style={{ width: "100%" }}>
                    <h3 style={{ marginTop: "0px", marginBottom: "30px", width: "100%", borderBottom: "2px solid" + config.baseColor, color: config.baseColor }}>
                        {consumption ? translate('custom.edit_consumption_item') : translate('custom.add_consumption_item')}
                    </h3>
                </div>
                
                <Grid container spacing={2}>
                    {/* Name Field */}
                    <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                        {disableAutocomplete ? 
                            <TextField
                                fullWidth
                                required
                                error={!!errors.name}
                                label={translate('custom.name')}
                                value={formData.name}
                                onChange={(e) => handleChange('name', e.target.value)}
                                variant="standard"
                                inputRef={nameInputRef}
                                helperText={errors.name ? errors.name : null}
                            />
                            :
                            <Autocomplete
                                fullWidth
                                options={existingConsumptions.map(c => c.name)}
                                onChange={handleNameChange}
                                onKeyDown={handleAutoCompleteKeyDown}
                                onBlur={() => setDisableAutocomplete(true)}
                                inputValue = {formData.name}
                                onInputChange = {(e) => handleChange('name', e?.target?.value)}
                                renderInput={(params) => <TextField 
                                    {...params}
                                    required 
                                    variant="standard"
                                    label={translate('custom.name')}
                                    inputRef={autoCompleteRef}
                                />}
                            />
                        }
                    </Grid>
                    
                    {/* Price Field */}
                    <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                        <TextField
                            fullWidth
                            required
                            error={!!errors.price}
                            label={translate('custom.price')}
                            type="number"
                            value={formData.price}
                            onChange={(e) => handleChange('price', e.target.value)}
                            variant="standard"
                            InputProps={{
                                endAdornment: <InputAdornment position="end">{currencyCode}</InputAdornment>,
                                style: { fontWeight: 'bold' }
                            }}
                            helperText={(errors.price ? errors.price + ". " : "") + translate('custom.TEXT.price_is_excluding_deposit_amount_if_any')}
                        />
                    </Grid>
                    
                    {/* Minimum Age Field */}
                    <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                        <FormControl fullWidth variant="standard">
                            <InputLabel>{translate('custom.minimum_age')}</InputLabel>
                            <Select
                                value={formData.min_age}
                                onChange={(e) => handleChange('min_age', e.target.value)}
                            >
                                {getChoices(config.ages).map(age => (
                                    <MenuItem key={age.id} value={age.id}>
                                        {age.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    
                    {/* Color Field */}
                    <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                        <ColorSelect
                            value={formData.color}
                            onChange={(value) => handleChange('color', value)}
                            colors={config.consumption_colors}
                            label={translate('custom.color')}
                        />
                    </Grid>
                    
                    {/* Type Field */}
                    <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                        <FormControl fullWidth variant="standard">
                            <InputLabel>{translate('custom.item_type')}</InputLabel>
                            <Select
                                value={formData.type}
                                onChange={(e) => handleChange('type', e.target.value)}
                            >
                                {consumption_types.map(type => (
                                    <MenuItem key={type.id} value={type.id}>
                                        {type.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    
                    {/* Round Field (Conditional) */}
                    {formData.type === 'input' && (
                    <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                        <FormControl fullWidth variant="standard">
                            <InputLabel>{translate('custom.round')}</InputLabel>
                            <Select
                                value={formData.round}
                                onChange={(e) => handleChange('round', e.target.value)}
                                endAdornment={
                                  formData.round && (
                                    <InputAdornment position="end">
                                      <IconButton
                                        size="small"
                                        sx={{marginRight: "20px"}}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleChange('round', '');
                                        }}
                                      >
                                        <Clear fontSize="small" />
                                      </IconButton>
                                    </InputAdornment>
                                  )
                                }
                            >
                                {getChoices(config.round, 'currency', translate('custom.cents'), currencyCode).map(round => (
                                    <MenuItem key={round.id} value={round.id}>
                                        {round.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    )}
                    
                    {/* Deposit Field (Conditional) */}
                    {formData.type === 'quantity' && (
                        <Grid item xs={12} sm={12} md={12} lg={4} xl={4}>
                            <FormControl fullWidth variant="standard">
                            <InputLabel>{translate('custom.deposit')}</InputLabel>
                            <Select
                                value={formData.deposit}
                                onChange={(e) => handleChange('deposit', e.target.value)}
                                endAdornment={
                                  formData.deposit && (
                                    <InputAdornment position="end">
                                      <IconButton
                                        size="small"
                                        sx={{marginRight: "20px"}}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleChange('deposit', '');
                                        }}
                                      >
                                        <Clear fontSize="small" />
                                      </IconButton>
                                    </InputAdornment>
                                  )
                                }
                            >
                                {deposits.map(deposit => (
                                    <MenuItem key={deposit.id} value={deposit.id}>
                                        <p style={{margin: "0px", fontSize: "13px"}}>{deposit.name} {translate('custom.of')} {deposit.price} {currencyCode}</p>
                                    </MenuItem>
                                ))}
                            </Select>
                            </FormControl>
                        </Grid>
                    )}
                    
                    {/* Hidden Flag */}
                    <Grid item xs={12} sm={12} md={12} lg={4} xl={4}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.hidden}
                                    onChange={(e) => handleChange('hidden', e.target.checked)}
                                />
                            }
                            label={translate('custom.hidden')}
                        />
                    </Grid>
                    
                    {/* Can Buy With Voucher Flag */}
                    <Grid item xs={12} sm={12} md={12} lg={4} xl={4}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.can_buy_only_with_voucher}
                                    onChange={(e) => handleChange('can_buy_only_with_voucher', e.target.checked)}
                                />
                            }
                            label={translate('custom.can_buy_only_with_voucher')}
                        />
                    </Grid>
                    
                    {/* Tag Field */}
                    <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                        <TextField
                            fullWidth
                            label={translate('custom.tag')}
                            value={formData.tag}
                            onChange={(e) => handleChange('tag', e.target.value)}
                            variant="standard"
                        />
                    </Grid>
                    
                    {/* Start Date Field */}
                    <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                        <TextField
                            fullWidth
                            type="datetime-local"
                            label={translate('custom.start_date')}
                            value={formData.start_date}
                            onChange={(e) => handleChange('start_date', e.target.value)}
                            variant="standard"
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                    </Grid>
                    
                    <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                        <TextField
                            fullWidth
                            type="datetime-local"
                            label={translate('custom.end_date')}
                            value={formData.end_date}
                            onChange={(e) => handleChange('end_date', e.target.value)}
                            variant="standard"
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                    </Grid>
                </Grid>
                
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    {
                        !consumption ? <div></div> :
                        <Button onClick={handleDelete} color="error" variant="outlined" startIcon={<Delete />} 
                            sx={{
                              "& .button-text": {
                                display: { xs: "none", sm: "inline" }
                              },
                              "& .MuiButton-startIcon": {
                                marginRight: { xs: 0, sm: 1 }
                              }
                            }}
                        >
                            <span className='button-text'>{translate('custom.delete')}</span>
                        </Button>
                    }
                    <div style={{display: "flex", gap: 10}}>
                        <Button onClick={handleCancel} variant="outlined" startIcon={<Close />}
                            sx={{
                              "& .button-text": {
                                display: { xs: "none", sm: "inline" }
                              },
                              "& .MuiButton-startIcon": {
                                marginRight: { xs: 0, sm: 1 }
                              }
                            }}
                        >
                            <span className='button-text'>{translate('custom.cancel')}</span>
                            
                        </Button>
                        <Button variant="contained" color="primary" onClick={handleSave} startIcon={<Save />}>
                            {translate('custom.save')}
                        </Button>
                    </div>
                </Box>
            </Box>
        </Modal>
    );
};

export default ConsumptionForm;
